import type {
  SignatureEnvelopeStatus,
  SignatureRecipientStatus,
} from '@commenda/prisma';

type BadgeVariant = 'gray' | 'warning' | 'blue' | 'success' | 'error';

type StatusDisplay = {
  label: string;
  variant: BadgeVariant;
};

/**
 * User-facing task status per Linear spec. "Declined" is never shown; it becomes "Canceled".
 * "Failed" means the signing email could not be delivered (bounce / invalid address).
 */
export enum ESignTaskStatus {
  Pending = 'Pending',
  Sent = 'Sent',
  Viewed = 'Viewed',
  Signed = 'Signed',
  Failed = 'Failed',
  Canceled = 'Canceled',
}

export class ESignatureHelper {
  static ENVELOPE_STATUS_DISPLAY: Record<
    SignatureEnvelopeStatus,
    StatusDisplay
  > = {
    CREATED: { label: 'Draft', variant: 'gray' },
    SENT: { label: 'Sent', variant: 'warning' },
    DELIVERED: { label: 'Viewed', variant: 'blue' },
    COMPLETED: { label: 'Completed', variant: 'success' },
    DECLINED: { label: 'Declined', variant: 'error' },
    VOIDED: { label: 'Cancelled', variant: 'gray' },
    FAILED: { label: 'Failed', variant: 'error' },
  };

  static RECIPIENT_STATUS_DISPLAY: Record<
    SignatureRecipientStatus,
    StatusDisplay
  > = {
    PENDING: { label: 'Pending', variant: 'gray' },
    SENT: { label: 'Sent', variant: 'warning' },
    DELIVERED: { label: 'Viewed', variant: 'blue' },
    SIGNED: { label: 'Signed', variant: 'success' },
    DECLINED: { label: 'Canceled', variant: 'error' },
    FAILED: { label: 'Failed', variant: 'error' },
  };

  /** Display config for task-card statuses (spec: Pending, Sent, Viewed, Signed, Canceled). */
  static TASK_STATUS_DISPLAY: Record<ESignTaskStatus, StatusDisplay> = {
    [ESignTaskStatus.Pending]: { label: 'Pending', variant: 'gray' },
    [ESignTaskStatus.Sent]: { label: 'Sent', variant: 'warning' },
    [ESignTaskStatus.Viewed]: { label: 'Viewed', variant: 'blue' },
    [ESignTaskStatus.Signed]: { label: 'Signed', variant: 'success' },
    [ESignTaskStatus.Failed]: { label: 'Failed', variant: 'error' },
    [ESignTaskStatus.Canceled]: { label: 'Canceled', variant: 'error' },
  };

  /**
   * Effective user-facing status. VOIDED/DECLINED → Canceled; FAILED → Failed (delivery bounced).
   * Canceled is checked first, so an explicitly voided/declined request never reads as Failed.
   */
  static getTaskStatus(
    recipientStatus: SignatureRecipientStatus,
    envelopeStatus: SignatureEnvelopeStatus
  ): ESignTaskStatus {
    if (
      envelopeStatus === 'VOIDED' ||
      envelopeStatus === 'DECLINED' ||
      recipientStatus === 'DECLINED'
    ) {
      return ESignTaskStatus.Canceled;
    }
    if (envelopeStatus === 'FAILED' || recipientStatus === 'FAILED') {
      return ESignTaskStatus.Failed;
    }
    const map: Record<SignatureRecipientStatus, ESignTaskStatus> = {
      PENDING: ESignTaskStatus.Pending,
      SENT: ESignTaskStatus.Sent,
      DELIVERED: ESignTaskStatus.Viewed,
      SIGNED: ESignTaskStatus.Signed,
      DECLINED: ESignTaskStatus.Canceled,
      FAILED: ESignTaskStatus.Failed,
    };
    return map[recipientStatus];
  }

  /** Task header per spec: "Signature required: {doc}", "Signed: {doc}", "Canceled: {doc}". */
  static getTaskHeader(
    taskStatus: ESignTaskStatus,
    documentName: string
  ): string {
    const doc = documentName || 'Document';
    switch (taskStatus) {
      case ESignTaskStatus.Signed:
        return `Signed: ${doc}`;
      case ESignTaskStatus.Failed:
        return `Delivery failed: ${doc}`;
      case ESignTaskStatus.Canceled:
        return `Canceled: ${doc}`;
      case ESignTaskStatus.Pending:
      case ESignTaskStatus.Sent:
      case ESignTaskStatus.Viewed:
      default:
        return `Signature required: ${doc}`;
    }
  }

  static getClientTaskHeader(
    taskStatus: ESignTaskStatus,
    recipientName: string
  ): string {
    const name = recipientName || 'the signer';
    switch (taskStatus) {
      case ESignTaskStatus.Pending:
        return `Waiting to notify ${name}`;
      case ESignTaskStatus.Sent:
      case ESignTaskStatus.Viewed:
        return `Waiting for ${name} to sign`;
      case ESignTaskStatus.Signed:
        return `Signed by ${name}`;
      case ESignTaskStatus.Failed:
        return `Couldn't reach ${name}`;
      case ESignTaskStatus.Canceled:
        return 'Signature request canceled';
      default:
        return name;
    }
  }

  /** Task description per spec. No signing order stored — we use recipient status only. */
  static getTaskDescription(params: {
    taskStatus: ESignTaskStatus;
    recipientName: string;
    recipientEmail?: string;
    signedAt?: string | null;
    declinedByName?: string | null;
    /** Multiple signers on this envelope. */
    hasMultipleSigners?: boolean;
    /** Another recipient is SENT or DELIVERED (so this one is waiting on them). */
    hasAnotherSignerActive?: boolean;
  }): string {
    const {
      taskStatus,
      recipientName,
      recipientEmail = '',
      signedAt,
      declinedByName,
      hasMultipleSigners = false,
      hasAnotherSignerActive = false,
    } = params;
    switch (taskStatus) {
      case ESignTaskStatus.Pending:
        if (hasMultipleSigners && hasAnotherSignerActive)
          return `This will get sent when the other signer(s) complete.`;
        return `${recipientName} is waiting to be notified. Send the document to get started.`;
      case ESignTaskStatus.Sent:
        return `An email has been sent to ${recipientName}${recipientEmail ? ` (${recipientEmail})` : ''}. They can sign by following the link in their email.`;
      case ESignTaskStatus.Viewed:
        return `${recipientName} has opened the document but has not yet signed. They can sign by following the link in their email.`;
      case ESignTaskStatus.Signed:
        return signedAt
          ? `${recipientName} signed on ${formatSignedAt(signedAt)}.`
          : `${recipientName} signed.`;
      case ESignTaskStatus.Failed:
        return `The email to ${recipientName}${recipientEmail ? ` (${recipientEmail})` : ''} could not be delivered. Check the address is correct and send the request again.`;
      case ESignTaskStatus.Canceled:
        return declinedByName
          ? `This signature request has been canceled because ${declinedByName} declined to sign.`
          : 'This signature request has been canceled.';
      default:
        return `${recipientName}${recipientEmail ? ` (${recipientEmail})` : ''}`;
    }
  }

  static canCancelEnvelope(status: SignatureEnvelopeStatus): boolean {
    return status !== 'COMPLETED' && status !== 'VOIDED';
  }

  /** No reminder for PENDING (spec) or FAILED (bounced — resend instead); otherwise SENT/DELIVERED while envelope is not completed/voided/failed. */
  static canSendReminder(
    recipientStatus: SignatureRecipientStatus,
    envelopeStatus: SignatureEnvelopeStatus
  ): boolean {
    if (recipientStatus === 'PENDING') return false;
    return (
      recipientStatus !== 'SIGNED' &&
      recipientStatus !== 'DECLINED' &&
      recipientStatus !== 'FAILED' &&
      envelopeStatus !== 'COMPLETED' &&
      envelopeStatus !== 'VOIDED' &&
      envelopeStatus !== 'FAILED'
    );
  }

  static isDraft(status: SignatureEnvelopeStatus): boolean {
    return status === 'CREATED';
  }

  static findSignedDocument(
    documents: { SignedFile: { id: number; fileName: string } | null }[]
  ): { id: number; fileName: string } | undefined {
    return documents.find((d) => d.SignedFile != null)?.SignedFile ?? undefined;
  }

  static getEnvelopeTitle(
    documents: { SourceFile: { fileName: string } }[]
  ): string {
    return documents[0]?.SourceFile.fileName || 'Untitled envelope';
  }

  static readonly GENERIC_ERROR =
    'Something went wrong. Please try again or contact Commenda support.';

  static parseErrorMessage(_body: unknown, status: number): string {
    if (status === 429) return 'Reminder already sent today.';
    return ESignatureHelper.GENERIC_ERROR;
  }
}

function formatSignedAt(isoDate: string): string {
  try {
    const d = new Date(isoDate);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return isoDate;
  }
}
